import React, { useEffect } from "react";
import styles from "./selector-interface.module.scss";
import { useQuery } from "@tanstack/react-query";
import { useBridge } from "@/features/bridge/providers/bridge.provider";
import ModalPopper from "@components/layout/modal-popper/modal-popper";
import MultiTokenConfirmation from "@/features/bridge/components/token-confirmation/multi-token-confirmation";
import CollectionCard from "@/features/bridge/components/token-selector/selector-interface/collection-card";
import CardList from "@/features/bridge/components/token-selector/selector-interface/collection-list";
import { useMultiTokenManager } from "@/features/bridge/stores/multi-token-manager";
import { useTokenWindowControls } from "@/features/bridge/stores/token-window.store";
import { TokenType } from "@/features/bridge/types/token-selector.types";
import { fetchAllMultis } from "@/features/bridge/services/alchemy/getAllMultis";
import { collectionContains } from "@/features/bridge/utils/object-contains";
import BridgingHeader from "./bridging-header";
import { useTokenFilterStore } from "@/features/bridge/stores/token-filter.store";
import { useCollectionEligibility } from "@/features/bridge/hooks/useCollectionEligibility.hook";
import type { GeneralTokenCollectionType } from "@/features/bridge/types/open-sea-api.types";

export interface Token {
  id: string;
  name: string;
  image: string;
  collection: string;
}

// Component to check collection eligibility and conditionally render
const FilteredCollectionCard = ({
  collectionKey,
  collection,
  ...props
}: {
  collectionKey: string;
  collection: GeneralTokenCollectionType[string];
  collectionName: string;
  setActiveCollection: (collection: string) => void;
  activeTokenCollection: string | undefined;
  collectionAmount: number | undefined;
  selectionCount: number;
  isDisabled: boolean;
  clearTokenCollection: () => void;
  tokenType: "NFT" | "MULTI";
}) => {
  const { showOnlyBridgeableTokens } = useTokenFilterStore();
  const collectionEligibility = useCollectionEligibility(collection);

  // If filtering is enabled and collection has no eligible tokens, don't render
  if (showOnlyBridgeableTokens && !collectionEligibility.hasEligibleTokens) {
    return null;
  }

  return (
    <CollectionCard
      collection={collection}
      collectionKey={collectionKey}
      {...props}
    />
  );
};

const MultiSelectionWindow = () => {
  const { activeTokenWindowType, setActiveTokenWindowType } =
    useTokenWindowControls();
  const isOpen = activeTokenWindowType === TokenType.MULTI;

  const {
    bridgeControls: {
      account,
      fromChain: { originChain },
      toChain: { destinationChain },
    },
  } = useBridge();

  const {
    collections,
    setTokensFound,
    setActiveCollection,
    activeCollection,
    getSelectionCount,
    getActiveCollection,
    setCollections,
    selectAllTokens,
    deselectAllTokens,
    getAllSelectionsCount,
    deselectAllCollections,
    removeSuccessfullySentTokens,
    clearAndReset,
  } = useMultiTokenManager();

  const { data, status, isLoading, isFetching, refetch } = useQuery({
    queryKey: [`${originChain?.chainName}-multitoken`],
    queryFn: () => fetchAllMultis(account.address, originChain?.chainId),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: !!account.address && !!originChain?.chainId && isOpen,
  });

  useEffect(() => {
    clearAndReset();
  }, [originChain?.chainId, account.address, clearAndReset, refetch]);

  useEffect(() => {
    if (status === "success" && data) {
      console.log(data);
      setCollections(data);
      if (collectionContains(data)) setTokensFound(true);
      else setTokensFound(false);
    }
  }, [data, status, setCollections, setTokensFound]);

  return (
    <ModalPopper
      isOpen={isOpen}
      onClose={() => setActiveTokenWindowType(null)}
      destroyOnClose
    >
      <div className={styles.container}>
                <div className={styles.selectTokenHeader}>
          <BridgingHeader
            originChain={originChain}
            destinationChain={destinationChain}
          />
        </div>
        <div className={styles.wrapper}>
          <div className={styles.left}>
            <div className={styles.collections}>
              {Object.entries(collections).map(([collectionKey, collection], index) => {
                const collectionName = collection.collectionData?.name;
                const disabled =
                  !getSelectionCount(collectionKey) &&
                  !!getAllSelectionsCount();
                return (
                  <FilteredCollectionCard
                    key={collectionKey}
                    collectionKey={collectionKey}
                    collection={collection}
                    collectionName={collectionName}
                    setActiveCollection={setActiveCollection}
                    activeTokenCollection={activeCollection}
                    collectionAmount={collection.collectionData?.amount}
                    selectionCount={getSelectionCount(collectionKey)}
                    clearTokenCollection={deselectAllCollections}
                    isDisabled={disabled}
                    tokenType="MULTI"
                  />
                );
              })}
            </div>
          </div>
          <div className={styles.right}>
            <CardList
              isLoading={isLoading || isFetching}
              collections={collections}
              selectAllTokens={selectAllTokens}
              getSelectionCount={getAllSelectionsCount}
              refetchTokens={refetch}
            />
          </div>
          <div className={styles.checkoutPanel}>
            <MultiTokenConfirmation
              selectedCollection={getActiveCollection()}
              clearTokenSelection={deselectAllTokens}
              removeSentTokens={removeSuccessfullySentTokens}
            />
          </div>
        </div>
      </div>
    </ModalPopper>
  );
};

export default MultiSelectionWindow;
